import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="page-transition pt-20" data-testid="privacy-policy-page">
      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Legal
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Privacy Policy
              </h1>
              <p className="text-[#52525B]">Last Updated: December 2024</p>
            </div>

            <div className="prose prose-lg max-w-none text-[#52525B]">
              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Introduction</h2>
              <p>
                Primes Pick ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our pest control services.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Personal Information</h3>
              <p>We may collect personal information that you voluntarily provide, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name and contact information (email, phone number, address)</li>
                <li>Service inquiry details</li>
                <li>Payment information (processed securely through third-party providers)</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#1A1C1A] mt-6 mb-3">Automatically Collected Information</h3>
              <p>When you visit our website, we may automatically collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on site</li>
                <li>Referring website information</li>
              </ul>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>3. How We Use Your Information</h2>
              <p>We use the collected information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide and improve our pest control services</li>
                <li>Respond to inquiries and schedule appointments</li>
                <li>Send service reminders and promotional communications</li>
                <li>Process payments and maintain records</li>
                <li>Analyze website usage to improve user experience</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Information Sharing</h2>
              <p>We do not sell your personal information. We may share information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Service providers who assist in our operations</li>
                <li>Legal authorities when required by law</li>
                <li>Business partners with your consent</li>
              </ul>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access your personal information</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your information</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent where applicable</li>
              </ul>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Cookies</h2>
              <p>
                We use cookies and similar tracking technologies to enhance your browsing experience. You can control cookie preferences through your browser settings. For more information, see our <Link to="/cookie-policy" className="text-[#2E7D32] hover:underline">Cookie Policy</Link>.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party sites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>

              <h2 className="text-2xl font-bold text-[#1A1C1A] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <ul className="list-none space-y-2 mt-4">
                <li><strong>Email:</strong> support@primespick.store</li>
                <li><strong>Phone:</strong> +1-888-356-8891</li>
                <li><strong>Address:</strong> 1284 Silver Lake Road, Minneapolis, MN 44401</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
